import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError, downloadPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, User, Mail, Phone, Pencil, CalendarOff, X, FileDown, CalendarRange } from "lucide-react";
import { LeaveDialog } from "@/components/LeaveDialog";
import { RosterGrid } from "@/components/RosterGrid";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // index = Python weekday (0=Mon)

const toYmd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function PayPeriodCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const { data } = useQuery({
    queryKey: ["payroll-settings"],
    queryFn: async () => (await api.get("/payroll-settings")).data,
  });
  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const save = useMutation({
    mutationFn: (body) => api.put("/payroll-settings", body),
    onSuccess: () => {
      toast.success(t("Pay period saved"));
      qc.invalidateQueries({ queryKey: ["payroll-settings"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (!form) return null;
  return (
    <Card className="rounded-md border p-4" data-testid="pay-period-card">
      <div className="mb-1 flex items-center gap-2">
        <CalendarOff className="h-4 w-4 text-muted-foreground" />
        <span className="label-caps text-muted-foreground">{t("Pay period")}</span>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">{t("Timesheets are grouped and exported by this period.")}</p>
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs">{t("Period length")}</Label>
          <select
            value={form.pay_period}
            onChange={(e) => setForm({ ...form, pay_period: e.target.value })}
            data-testid="pay-period-select"
            className="block w-44 rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="weekly">{t("Weekly")}</option>
            <option value="fortnightly">{t("Fortnightly")}</option>
            <option value="monthly">{t("Monthly")}</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t("Week starts on")}</Label>
          <select
            value={form.week_start}
            onChange={(e) => setForm({ ...form, week_start: Number(e.target.value) })}
            disabled={form.pay_period === "monthly"}
            data-testid="week-start-select"
            className="block w-44 rounded-md border border-border bg-background px-3 py-2 text-sm disabled:opacity-50"
          >
            {WEEKDAYS.map((d, i) => (
              <option key={d} value={i}>{d}</option>
            ))}
          </select>
        </div>
        <Button onClick={() => save.mutate(form)} disabled={save.isPending} data-testid="save-pay-period-btn" className="gap-2">
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("Save")}
        </Button>
      </div>
    </Card>
  );
}

export default function Cleaners() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [finalPayBusy, setFinalPayBusy] = useState(null);
  const exportFinalPay = async (c) => {
    setFinalPayBusy(c.id);
    try {
      await downloadPdf(`/timesheets/final-pay/pdf?cleaner_id=${c.id}`, `final_pay_${c.name.replace(/[^A-Za-z0-9]+/g, "_")}.pdf`);
    } catch (e) {
      const d = e.response?.data;
      let msg = "";
      if (d instanceof Blob) { try { msg = JSON.parse(await d.text()).detail; } catch { /* ignore */ } }
      toast.error(msg || formatApiError(e));
    } finally { setFinalPayBusy(null); }
  };
  const [editing, setEditing] = useState(null);
  const [leaveFor, setLeaveFor] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });

  const { data: cleaners = [], isLoading } = useQuery({
    queryKey: ["cleaners", "all"],
    queryFn: async () => (await api.get("/cleaners?include_inactive=true")).data,
  });

  const create = useMutation({
    mutationFn: () => api.post("/cleaners", form),
    onSuccess: () => {
      toast.success(t("Cleaner added"));
      setOpen(false);
      setForm({ name: "", email: "", password: "", phone: "" });
      qc.invalidateQueries({ queryKey: ["cleaners"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const update = useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/cleaners/${id}`, body),
    onSuccess: (_r, v) => {
      toast.success(v.active === false ? t("Cleaner disabled") : v.active === true ? t("Cleaner enabled") : t("Cleaner updated"));
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["cleaners"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/cleaners/${id}`),
    onSuccess: () => {
      toast.success(t("Cleaner removed"));
      setToDelete(null);
      qc.invalidateQueries({ queryKey: ["cleaners"] });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold">{t("Cleaners")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("Team members who log in to the mobile app. Set phone, rostered days off and enable/disable access.")}
          </p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-cleaner-btn" className="k-press gap-2">
          <Plus className="h-4 w-4" /> {t("Add Cleaner")}
        </Button>
      </div>

      <PayPeriodCard />

      <Card className="rounded-md p-4" data-testid="roster-card">
        <div className="mb-2 flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-indigo-700" />
          <h2 className="text-base font-semibold md:text-lg">{t("Roster")}</h2>
          <span className="text-xs text-muted-foreground">{t("Active cleaners, by pay period. Previous periods are locked.")}</span>
        </div>
        <RosterGrid editable />
      </Card>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : cleaners.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 rounded-md border border-dashed py-20 text-center">
          <User className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t("No cleaners yet. Add your first team member.")}</p>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="cleaners-grid">
          {cleaners.map((c) => {
            const disabled = c.active === false;
            const offDays = [...(c.weekly_off || [])].sort((a, b) => a - b).map((d) => WEEKDAYS[d]);
            return (
              <Card
                key={c.id}
                data-testid={`cleaner-card-${c.id}`}
                className={`rounded-md border p-4 ${disabled ? "opacity-60" : ""}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary font-display font-bold">
                      {c.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 font-semibold">
                        {c.name}
                        {disabled && (
                          <span
                            data-testid={`disabled-badge-${c.id}`}
                            className="rounded-full border border-rose-300 bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-700"
                          >
                            {t("Disabled")}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Mail className="h-3 w-3" /> {c.email}
                      </div>
                      {c.phone ? (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Phone className="h-3 w-3" /> {c.phone}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {disabled && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1.5 text-xs"
                        disabled={finalPayBusy === c.id}
                        onClick={() => exportFinalPay(c)}
                        data-testid={`final-pay-pdf-${c.id}`}
                      >
                        {finalPayBusy === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
                        {t("Final pay PDF")}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setLeaveFor(c)}
                      data-testid={`leave-cleaner-${c.id}`}
                      title={t("Leave")}
                    >
                      <CalendarRange className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setEditing(c)}
                      data-testid={`edit-cleaner-${c.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => setToDelete(c)}
                      data-testid={`delete-cleaner-${c.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {(c.leave_periods || []).filter((lp) => lp.end_date >= toYmd(new Date())).length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs" data-testid={`cleaner-leave-chips-${c.id}`}>
                    <CalendarRange className="h-3.5 w-3.5 text-indigo-700" />
                    {(c.leave_periods || []).filter((lp) => lp.end_date >= toYmd(new Date())).map((lp) => (
                      <button key={lp.id} type="button" onClick={() => setLeaveFor(c)} className="rounded-full bg-indigo-100 px-2 py-0.5 font-medium text-indigo-800 hover:bg-indigo-200">
                        {t(lp.label)} {lp.start_date.slice(8)}/{lp.start_date.slice(5, 7)}{lp.end_date !== lp.start_date ? `–${lp.end_date.slice(8)}/${lp.end_date.slice(5, 7)}` : ""}
                      </button>
                    ))}
                  </div>
                )}
                {(offDays.length > 0 || (c.days_off || []).length > 0) && (
                  <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-dashed border-border pt-2.5 text-xs text-muted-foreground">
                    <CalendarOff className="h-3.5 w-3.5" />
                    {offDays.length > 0 && <span>{t("Off:")} {offDays.join(", ")}</span>}
                    {(c.days_off || []).length > 0 && (
                      <span className="text-muted-foreground">
                        {offDays.length > 0 ? " · " : ""}
                        {t("{{count}} date(s)", { count: c.days_off.length })}
                      </span>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Add cleaner */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-cleaner-dialog">
          <DialogHeader>
            <DialogTitle>{t("Add Cleaner")}</DialogTitle>
            <DialogDescription>{t("Create a login for a team member. They'll use the mobile app.")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("Full name")}</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="cleaner-name-input"
                placeholder="Aroha Ngata"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("Email")}</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="cleaner-email-input"
                placeholder="aroha@quantumlodge.co.nz"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("Phone (optional)")}</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="cleaner-phone-input"
                placeholder="+64 21 123 4567"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("Temporary password")}</Label>
              <Input
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="cleaner-password-input"
                placeholder={t("Set a password")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t("Cancel")}
            </Button>
            <Button
              onClick={() => create.mutate()}
              disabled={!form.name || !form.email || !form.password || create.isPending}
              data-testid="save-cleaner-btn"
              className="gap-2"
            >
              {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("Add cleaner")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LeaveDialog cleaner={leaveFor} onClose={() => setLeaveFor(null)} />

      <EditCleanerDialog
        cleaner={editing}
        pending={update.isPending}
        onClose={() => setEditing(null)}
        onSave={(body) => update.mutate({ id: editing.id, ...body })}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Remove {{name}}?", { name: toDelete?.name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("This permanently deletes their login. To keep records, use \"Disable\" instead. Existing timesheet records are kept.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => del.mutate(toDelete.id)}
              data-testid="confirm-delete-cleaner"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("Remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditCleanerDialog({ cleaner, onClose, onSave, pending }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [active, setActive] = useState(true);
  const [rosterEmail, setRosterEmail] = useState(true);
  const [payrollRef, setPayrollRef] = useState("");
  const [weeklyOff, setWeeklyOff] = useState([]);
  const [daysOff, setDaysOff] = useState([]);
  const [emp, setEmp] = useState({});

  useEffect(() => {
    if (cleaner) {
      setEmp(cleaner.employment || {});
      setName(cleaner.name || "");
      setEmail(cleaner.email || "");
      setPhone(cleaner.phone || "");
      setPassword("");
      setActive(cleaner.active !== false);
      setRosterEmail(cleaner.roster_email !== false);
      setPayrollRef(cleaner.payroll_ref || "");
      setWeeklyOff(cleaner.weekly_off || []);
      setDaysOff(cleaner.days_off || []);
    }
  }, [cleaner]);

  if (!cleaner) return null;

  const toggleWeekday = (idx) =>
    setWeeklyOff((prev) => (prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx]));
  const today = new Date().toISOString().slice(0, 10);
  const sixMonths = new Date();
  sixMonths.setMonth(sixMonths.getMonth() + 6);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="edit-cleaner-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("Edit {{name}}", { name: cleaner.name })}</DialogTitle>
          <DialogDescription>{t("Update details, roster and access.")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <div className="text-sm font-semibold">{t("Account active")}</div>
              <div className="text-xs text-muted-foreground">{t("Disabled cleaners can't log in and aren't assigned.")}</div>
            </div>
            <Switch checked={active} onCheckedChange={setActive} data-testid="cleaner-active-switch" />
          </div>
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <div className="text-sm font-semibold">{t("Weekly roster email")}</div>
              <div className="text-xs text-muted-foreground">{t("Email this cleaner their roster the evening before each new pay period starts.")}</div>
            </div>
            <Switch checked={rosterEmail} onCheckedChange={setRosterEmail} data-testid="cleaner-roster-email-switch" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("Name")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-cleaner-name" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("Phone")}</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="edit-cleaner-phone" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t("Login email")}</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="edit-cleaner-email"
            />
            <p className="text-xs text-muted-foreground">{t("Changing this updates the cleaner's login email.")}</p>
          </div>

          <div className="space-y-1.5">
            <Label>{t("Reset password (optional)")}</Label>
            <Input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("Leave blank to keep current")}
              data-testid="edit-cleaner-password"
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t("Payroll / employee reference")}</Label>
            <Input
              value={payrollRef}
              onChange={(e) => setPayrollRef(e.target.value)}
              placeholder={t("e.g. payroll ID / employee no.")}
              data-testid="edit-cleaner-payroll-ref"
            />
            <p className="text-xs text-muted-foreground">{t("Links this person to your payroll / compliance system. Shown on leave requests.")}</p>
          </div>

          <div className="space-y-2">
            <Label>{t("Weekly days off")}</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="weekly-off-picker">
              {WEEKDAYS.map((d, idx) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleWeekday(idx)}
                  data-testid={`weekday-${idx}`}
                  className={`k-press rounded-md border px-3 py-1.5 text-sm font-semibold ${
                    weeklyOff.includes(idx)
                      ? "border-rose-300 bg-rose-100 text-rose-700"
                      : "border-border text-muted-foreground hover:bg-secondary"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <EmploymentFields emp={emp} setEmp={setEmp} />

          <div className="space-y-2">
            <Label>{t("Specific days off (click to select, click again to remove — up to 6 months ahead)")}</Label>
            <div className="flex justify-center rounded-md border border-border">
              <Calendar
                mode="multiple"
                selected={daysOff.map((s) => new Date(s + "T00:00:00"))}
                onSelect={(dates) => setDaysOff((dates || []).map(toYmd).sort())}
                disabled={{ before: new Date(today + "T00:00:00"), after: sixMonths }}
                data-testid="days-off-calendar"
              />
            </div>
            {daysOff.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="days-off-list">
                {daysOff.map((d) => (
                  <span
                    key={d}
                    className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-800"
                    data-testid={`dayoff-chip-${d}`}
                  >
                    {d}
                    <button type="button" onClick={() => setDaysOff(daysOff.filter((x) => x !== d))} className="k-press">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            disabled={pending || !name || !email}
            onClick={() =>
              onSave({
                name,
                email,
                phone,
                active,
                roster_email: rosterEmail,
                payroll_ref: payrollRef,
                weekly_off: weeklyOff,
                days_off: daysOff,
                employment: emp,
                ...(password ? { password } : {}),
              })
            }
            data-testid="save-edit-cleaner-btn"
            className="gap-2"
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMP_TYPES = ["full_time", "part_time", "casual", "fixed_term", "minijob", "contractor"];

function EmploymentFields({ emp, setEmp }) {
  const { t } = useTranslation();
  const set = (k, v) => setEmp((e) => ({ ...e, [k]: v }));
  return (
    <div className="space-y-2 rounded-md border p-3" data-testid="employment-record">
      <div className="label-caps text-muted-foreground">{t("Employment record (for timesheet compliance)")}</div>
      <p className="text-[11px] text-muted-foreground">{t("Optional. Printed on the timesheet PDF when enabled in Timesheets → Record requirements. Pay details stay in payroll.")}</p>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">{t("Employment type")}</Label>
          <Select value={emp.employment_type || ""} onValueChange={(v) => set("employment_type", v)}>
            <SelectTrigger className="h-9" data-testid="emp-type"><SelectValue placeholder={t("Select")} /></SelectTrigger>
            <SelectContent>{EMP_TYPES.map((k) => <SelectItem key={k} value={k}>{t(`emp_${k}`)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("Occupation")}</Label>
          <Input value={emp.occupation || ""} onChange={(e) => set("occupation", e.target.value)} placeholder={t("Housekeeper / Cleaner")} data-testid="emp-occupation" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("Start date")}</Label>
          <Input type="date" value={emp.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="emp-start-date" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("Date of birth")}</Label>
          <Input type="date" value={emp.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="emp-dob" />
        </div>
        <div className="col-span-2 space-y-1">
          <Label className="text-xs">{t("Home address")}</Label>
          <Input value={emp.home_address || ""} onChange={(e) => set("home_address", e.target.value)} data-testid="emp-address" />
        </div>
        <label className="flex items-center justify-between gap-2 rounded-md border p-2 text-xs">
          <span>{t("UK 48-hour opt-out signed")}</span>
          <Switch checked={!!emp.wtr_opt_out} onCheckedChange={(v) => set("wtr_opt_out", v)} data-testid="emp-wtr-opt-out" />
        </label>
        <label className="flex items-center justify-between gap-2 rounded-md border p-2 text-xs">
          <span>{t("Night worker")}</span>
          <Switch checked={!!emp.night_worker} onCheckedChange={(v) => set("night_worker", v)} data-testid="emp-night-worker" />
        </label>
      </div>
    </div>
  );
}
